// Small colored pill shown in the Restaurant Dashboard header to tell the
// owner which print bridge is currently active. Reads live state from
// `nb55BridgeKind()` (see /app/frontend/src/utils/nb55Printer.js).
//
// Four states, each with its own color + Arabic label:
//   • webview — ✅ Direct JS→Kotlin bridge (preferred, no network hop)
//   • vendor  — 🔵 Legacy OEM JS bridge (NB55/Sunmi/etc. ROM-provided)
//   • http    — 🟡 Standalone BridgeService APK on 127.0.0.1:8899
//   • none    — 🔴 Nothing — browser-print only
//
// Clicking the badge navigates to /printer-setup so the owner can take
// action if the state is anything other than webview.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { nb55BridgeKind, refreshNyxBridge } from "@/utils/nb55Printer";
import { CheckCircle2, Wifi, Smartphone, XCircle } from "lucide-react";

const KIND_META = {
  webview: {
    label: "جسر WebView",
    sub: "طباعة مباشرة ✓",
    icon: Smartphone,
    tone: "bg-emerald-50 text-emerald-700 border-emerald-300",
    dot: "bg-emerald-500",
    priority: 3,
  },
  vendor: {
    label: "جسر الجهاز",
    sub: "ROM OEM",
    icon: CheckCircle2,
    tone: "bg-sky-50 text-sky-700 border-sky-300",
    dot: "bg-sky-500",
    priority: 2,
  },
  http: {
    label: "جسر محلي",
    sub: "localhost:8899",
    icon: Wifi,
    tone: "bg-amber-50 text-amber-700 border-amber-300",
    dot: "bg-amber-500",
    priority: 1,
  },
  none: {
    label: "لا طابعة",
    sub: "اضغط للإعداد",
    icon: XCircle,
    tone: "bg-red-50 text-red-700 border-red-300",
    dot: "bg-red-500",
    priority: 0,
  },
};

export default function BridgeKindBadge({ compact = false }) {
  // Re-check every 20s and after visibility-change so an operator who
  // just installed the APK sees the badge turn green without a reload.
  const [kind, setKind] = useState(() => nb55BridgeKind());

  useEffect(() => {
    let timer = null;
    const check = async () => {
      // Refresh the HTTP probe so transitions from http → none (and back)
      // reflect reality — JS-bridge detection is already synchronous.
      try { await refreshNyxBridge(); } catch { /* noop */ }
      setKind(nb55BridgeKind());
    };
    check();
    timer = setInterval(check, 20_000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { if (timer) clearInterval(timer); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const meta = KIND_META[kind] || KIND_META.none;
  const Icon = meta.icon;

  if (compact) {
    return (
      <Link
        to="/printer-setup"
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-black transition hover:opacity-90 ${meta.tone}`}
        title={`${meta.label} — ${meta.sub}`}
        data-testid={`bridge-badge-${kind}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} animate-pulse`}/>
        <Icon size={11}/>
        <span>{meta.label}</span>
      </Link>
    );
  }

  return (
    <Link
      to="/printer-setup"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 text-xs font-black transition hover:opacity-90 ${meta.tone}`}
      title="اضغط لفتح إعدادات الطابعة"
      data-testid={`bridge-badge-${kind}`}
    >
      <span className={`w-2 h-2 rounded-full ${meta.dot} ${kind === "webview" ? "animate-pulse" : ""}`}/>
      <Icon size={14}/>
      <div className="flex flex-col items-start leading-tight">
        <span style={{ fontFamily: "Cairo" }}>{meta.label}</span>
        <span className="text-[9px] font-normal opacity-70">{meta.sub}</span>
      </div>
    </Link>
  );
}
